import { NetworkStatus, useQuery } from '@apollo/client';
import {
  IconButton,
  IDropdownOption,
  PrimaryButton,
  Stack,
  Text,
} from '@fluentui/react';
import NOTIFY_TIMES from './NotifyTimes.graphql';
import {
  NotifyTimes,
  NotifyTimes_notifyTimes_nodes,
  NotifyTimesVariables,
} from './__generated__/NotifyTimes';
import { TABLE_ROWS } from 'common/constants';
import { useStyles } from './index.styles';
import { useCommonStyles } from 'common/styles';
import { ColumnData } from 'common/components/SearchBar';
import { useCallback, useEffect, useRef, useState } from 'react';
import { OrderDirection, SortOrder } from 'common/utils/commonTypes';
import { ColumnStylesProps, onRenderItems } from './columns.render';
import { notifyCycleFilterVariable, onHandleSearch } from './utils';
import { getSortedColumns } from 'common/utils/columnUtilities';
import { HelpIcon } from 'common/trainingSystem/HelpIcon';
import { InfiniteList } from 'common/components/InfiniteList';
import { NotificationCycleModal } from '../view/NotificationCycleModal';
import { DeleteNotifyCycle } from './DeleteNotifyCycle';
import { ActionBar } from './ActionBar';
import NOTIFY_TYPES from '../view/NotificationCycleModal/BasicForm/NotifyTypes.graphql';
import { NotifyTypes } from '../view/NotificationCycleModal/BasicForm/__generated__/NotifyTypes';
import { formatDropdownOptions } from 'common/utils';
import { FilterArrayType } from 'common/components/Filters';
import { NotifyTimeFilter } from 'common/types/globalTypes';
import { useColumns } from './column.data';

export type NotificationCycleRowItem = NotifyTimes_notifyTimes_nodes;

export const NotificationCycles: React.FC = () => {
  const styles = useStyles();
  const commonStyles = useCommonStyles();
  const { columns } = useColumns();
  const [gridColumns, setGridColumns] = useState<ColumnData[]>(columns);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [sortOrderParam, setSortOrderParam] = useState<SortOrder>();
  const [searchFilters, setSearchFilters] = useState<FilterArrayType[]>();
  const filters: NotifyTimeFilter | undefined = {
    and: notifyCycleFilterVariable(searchFilters) as NotifyTimeFilter[],
  };
  const { data: notifyTypesData } = useQuery<NotifyTypes>(NOTIFY_TYPES, {
    variables: {
      orderBy: ['SORT_ORDER_ASC'],
    },
  });
  const renderRef = useRef(false);
  const columnStyles: ColumnStylesProps = {
    onRenderColumnStack: styles.onRenderColumnStack,
  };
  const [selectedRows, setSelectedRows] = useState<NotificationCycleRowItem[]>(
    []
  );
  const {
    data: notifyTimesData,
    loading: notifyTimesLoading,
    error: notifyTimesError,
    networkStatus,
    refetch,
    fetchMore,
  } = useQuery<NotifyTimes, NotifyTimesVariables>(NOTIFY_TIMES, {
    variables: {
      first: TABLE_ROWS,
      orderBy: ['SORT_ORDER_ASC', '_CREATED_DATE_ASC'],
      filter: searchFilters?.length ? filters : undefined,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const refetching =
    networkStatus === NetworkStatus.refetch ||
    networkStatus === NetworkStatus.setVariables;

  const { nodes, pageInfo } = {
    ...notifyTimesData?.notifyTimes,
  };
  const { hasNextPage } = { ...pageInfo };
  const transformedData: NotificationCycleRowItem[] = refetching
    ? []
    : nodes?.map((item) => ({
        ...item,
      })) || [];
  const handleSearch = async (showMore: boolean) => {
    const { queryVariables } = onHandleSearch(
      showMore,
      sortOrderParam,
      notifyTimesData
    );
    if (showMore) fetchMore?.({ variables: queryVariables });
    else {
      refetch(queryVariables);
    }
  };

  const handleSearchMemo = useCallback(handleSearch, [sortOrderParam]);
  const _onColumnClick = (clickedColumn: ColumnData) => {
    const { newColumns, desc } = getSortedColumns(clickedColumn, gridColumns);
    setGridColumns(newColumns);
    setSortOrderParam({
      column: clickedColumn.key,
      direction: desc ? OrderDirection.DESC : OrderDirection.ASC,
    });
  };

  const _renderItemColumn = useCallback(onRenderItems(columnStyles), [
    columnStyles,
  ]);

  const setCustomColumns = useCallback(() => {
    setGridColumns(columns);
  }, []);

  useEffect(() => {
    setCustomColumns();
  }, [setCustomColumns]);

  const onRefresh = () => {
    refetch();
  };

  useEffect(() => {
    if (renderRef.current) handleSearchMemo(false);
    else renderRef.current = true;
  }, [handleSearchMemo]);

  const notifyTypeOptions: IDropdownOption[] =
    formatDropdownOptions(notifyTypesData?.notifyTypes?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.description || '',
      includeAll: true,
    }) || [];

  return (
    <>
      <Stack
        tokens={{ childrenGap: 20 }}
        className={commonStyles.listHeaderContainer}
      >
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
          className={commonStyles.listTitleContainer}
        >
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 7 }}>
            <Text variant="xLarge">Notification Cycles</Text>
            <HelpIcon appLinkType="NOTIFICATION_TIMES" />
          </Stack>
          <Stack verticalAlign="center" horizontal tokens={{ childrenGap: 30 }}>
            <DeleteNotifyCycle selectedRows={selectedRows} />
            <ActionBar
              onFilterChange={(filters) => {
                setSearchFilters(filters);
              }}
              notifyTypesOptions={notifyTypeOptions}
            />
            <IconButton
              iconProps={{ iconName: 'refresh' }}
              onClick={onRefresh}
              className={styles.iconStyle}
            />
            <PrimaryButton
              iconProps={{
                iconName: 'Add',
              }}
              text="New Cycle"
              onClick={() => setIsOpen(true)}
            />
          </Stack>
        </Stack>
      </Stack>
      <InfiniteList
        loading={notifyTimesLoading || (!notifyTimesData && !notifyTimesError)}
        items={transformedData}
        hasNextPage={hasNextPage}
        columns={gridColumns.filter((_column) => _column.isVisible)}
        onRenderItemColumn={_renderItemColumn}
        onColumnHeaderClick={(_, column) => {
          if (column) _onColumnClick(column);
        }}
        onSelectionChanged={setSelectedRows}
        onLoadMore={async () => {
          await handleSearch(true);
        }}
      />
      {isOpen && <NotificationCycleModal onClose={() => setIsOpen(false)} />}
    </>
  );
};
